import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Globe2 } from "lucide-react";

type GeoState = { loading: boolean; blocked: boolean; country: string | null };

/**
 * CountryBlockGuard
 *
 * Wraps the app and queries the `geo-check` edge function once on mount.
 * If the visitor's country is in the Owner-managed blocklist, replace the
 * entire UI with a generic "service unavailable in your region" page —
 * NEVER expose internal app details, route names, or branding metadata.
 *
 * Failures (network error, no country header) fall open by design so
 * legitimate users in non-CDN environments are not locked out.
 */
export function CountryBlockGuard({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<GeoState>({ loading: true, blocked: false, country: null });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await supabase.functions.invoke("geo-check", {
          body: { path: window.location.pathname },
        });
        if (cancelled) return;
        if (error || !data) {
          setState({ loading: false, blocked: false, country: null });
          return;
        }
        setState({
          loading: false,
          blocked: !!data.blocked,
          country: data.country ?? null,
        });
      } catch {
        if (!cancelled) setState({ loading: false, blocked: false, country: null });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Fail open while loading — don't flash a block page on slow networks
  if (state.loading) return <>{children}</>;

  if (state.blocked) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-6">
        <div className="max-w-md text-center space-y-5">
          <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-muted">
            <Globe2 className="h-8 w-8 text-muted-foreground" />
          </div>
          <h1 className="text-2xl font-semibold tracking-tight">Service unavailable</h1>
          <p className="text-sm text-muted-foreground leading-relaxed">
            This service is not available in your region at this time.
          </p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
